import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import type { RootState } from "store";

import CloseButton from "components/layout/CloseButton";
import FunctionalButton from "components/layout/FunctionalButton";
import AnimatedModal from "components/layout/motion/AnimatedModal";
import {
  type AlertCriteriaForm,
  type DataMatchRow,
  DETECTION_CLASS_ID_PATH,
  criteriaToForm,
  emptyAlertCriteriaForm,
  formToCriteria,
} from "lib/mission/emailAlertCriteriaForm";
import {
  EmailTemplate,
  LogEmailRule,
  createLogEmailRule,
  updateLogEmailRule,
  type CreateLogEmailRuleInput,
  type UpdateLogEmailRuleInput,
} from "lib/mission/emailNotificationsApi";

const fieldClass =
  "w-full rounded-r5 border border-black80 bg-black70 px-3 py-2.5 text-h5 text-white placeholder:text-black60 focus:border-primary2 focus:outline-none";

const DEFAULT_DETECTION_TOPIC = "/rtsp_inference/result";

interface EmailRuleDialogProps {
  visible: boolean;
  initial?: LogEmailRule | null;
  templates: EmailTemplate[];
  onClose: () => void;
  onSaved: () => void;
}

const EmailRuleDialog: React.FC<EmailRuleDialogProps> = ({
  visible,
  initial,
  templates,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation(["settings", "common"]);
  const { arcId, arcProxyHost } = useSelector((state: RootState) => state.arcProxy);
  const [name, setName] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [criteriaForm, setCriteriaForm] = useState<AlertCriteriaForm>(() => emptyAlertCriteriaForm());
  const [additionalRecipientsTo, setAdditionalRecipientsTo] = useState("");
  const [maxAttempts, setMaxAttempts] = useState(3);
  const [enabled, setEnabled] = useState(true);
  const [extractClip, setExtractClip] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  useEffect(() => {
    if (visible) {
      if (initial) {
        setName(initial.name ?? "");
        setTemplateId(initial.templateId ?? templates[0]?.id ?? "");
        setCriteriaForm(criteriaToForm(initial.criteria as Record<string, unknown> | undefined));
        setAdditionalRecipientsTo(initial.additionalRecipients?.to?.join(", ") ?? "");
        setMaxAttempts(initial.maxAttempts ?? 3);
        setEnabled(initial.enabled ?? true);
        setExtractClip(initial.extractClip ?? false);
      } else {
        setName("");
        setTemplateId(templates[0]?.id ?? "");
        setCriteriaForm({
          ...emptyAlertCriteriaForm(),
          topic: DEFAULT_DETECTION_TOPIC,
          matchEnabledClasses: true,
        });
        setAdditionalRecipientsTo("");
        setMaxAttempts(3);
        setEnabled(true);
        setExtractClip(false);
      }
      setAdvancedOpen(false);
      setSaving(false);
      setError(null);
    }
  }, [visible, initial, templates]);

  const updateDataRow = useCallback((index: number, patch: Partial<DataMatchRow>) => {
    setCriteriaForm((prev) => ({
      ...prev,
      dataRows: prev.dataRows.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    }));
  }, []);

  const addDataRow = useCallback(() => {
    setCriteriaForm((prev) => ({
      ...prev,
      dataRows: [...prev.dataRows, { path: "", valuesText: "" }],
    }));
  }, []);

  const removeDataRow = useCallback((index: number) => {
    setCriteriaForm((prev) => {
      const next = prev.dataRows.filter((_, i) => i !== index);
      return {
        ...prev,
        dataRows: next.length > 0 ? next : [{ path: "", valuesText: "" }],
      };
    });
  }, []);

  const handleSubmit = useCallback(async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError(t("emailNotifications.nameRequired"));
      return;
    }
    if (!templateId) {
      setError(t("emailNotifications.templateRequired"));
      return;
    }

    const parsedCriteria = formToCriteria(criteriaForm);
    const toList = additionalRecipientsTo
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    setSaving(true);
    setError(null);
    const additionalRecipients = { to: toList, cc: [] as string[], bcc: [] as string[] };

    try {
      if (initial) {
        const input: UpdateLogEmailRuleInput = {
          name: trimmedName,
          enabled,
          templateId,
          criteria: parsedCriteria,
          additionalRecipients,
          maxAttempts,
          extractClip,
        };
        await updateLogEmailRule(initial.id, input, arcProxyHost, arcId);
      } else {
        const input: CreateLogEmailRuleInput = {
          name: trimmedName,
          enabled,
          templateId,
          criteria: parsedCriteria,
          additionalRecipients,
          maxAttempts,
          extractClip,
        };
        await createLogEmailRule(input, arcProxyHost, arcId);
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : t("emailNotifications.saveFailed"));
    } finally {
      setSaving(false);
    }
  }, [
    name,
    templateId,
    criteriaForm,
    additionalRecipientsTo,
    maxAttempts,
    enabled,
    extractClip,
    initial,
    onSaved,
    onClose,
    arcProxyHost,
    arcId,
    t,
  ]);

  const isEdit = Boolean(initial);

  return (
    <AnimatedModal
      visible={visible}
      onClose={onClose}
      className="max-h-[min(90vh,40rem)] w-full max-w-lg overflow-y-auto rounded-r10 border border-black80 bg-black90 p-4"
      zIndexClassName="z-[10050]"
    >
      <div className="mb-4 flex items-center justify-between">
        <p className="text-h4 font-semibold text-white">
          {isEdit ? t("emailNotifications.ruleDialogEdit") : t("emailNotifications.ruleDialogNew")}
        </p>
        <CloseButton onClick={onClose} className="relative shrink-0" />
      </div>

      <div className="flex flex-col gap-4">
        <section className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <label className="text-h5 text-white">{t("emailNotifications.ruleName")}</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("emailNotifications.ruleNamePlaceholder")}
              className={fieldClass}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-h5 text-white">{t("emailNotifications.ruleTemplate")}</label>
            {templates.length === 0 ? (
              <p className="text-h5 text-black60">{t("emailNotifications.ruleTemplateEmpty")}</p>
            ) : (
              <select
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value)}
                className={fieldClass}
              >
                <option value="">{t("emailNotifications.ruleTemplatePlaceholder")}</option>
                {templates.map((tmpl) => (
                  <option key={tmpl.id} value={tmpl.id}>
                    {tmpl.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          <label className="flex items-center gap-2 text-h5 text-white">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="h-5 w-5 rounded accent-primary2"
            />
            {t("emailNotifications.ruleEnabled")}
          </label>

          <label className="flex items-center gap-2 text-h5 text-white">
            <input
              type="checkbox"
              checked={extractClip}
              onChange={(e) => setExtractClip(e.target.checked)}
              className="h-5 w-5 rounded accent-primary2"
            />
            {t("emailNotifications.saveAlertClip")}
          </label>

          {criteriaForm.matchEnabledClasses ? (
            <p className="text-h5 text-black60">{t("emailNotifications.ruleFollowsOrHint")}</p>
          ) : null}
        </section>

        <section className="border-t border-black80 pt-4">
          <button
            type="button"
            className="flex w-full items-center justify-between text-left text-h5 font-semibold text-white"
            onClick={() => setAdvancedOpen((o) => !o)}
            aria-expanded={advancedOpen}
          >
            <span>{t("emailNotifications.advancedSettings")}</span>
            <span className="text-h5 text-black40">{advancedOpen ? "▾" : "▸"}</span>
          </button>

          {advancedOpen ? (
            <div className="mt-3 flex flex-col gap-3">
              <div className="flex flex-col gap-1.5">
                <label className="text-h5 text-white">{t("emailNotifications.matchTopic")}</label>
                <input
                  type="text"
                  value={criteriaForm.topic}
                  onChange={(e) =>
                    setCriteriaForm((prev) => ({ ...prev, topic: e.target.value }))
                  }
                  placeholder={t("emailNotifications.matchTopicPlaceholder")}
                  className={fieldClass}
                />
                <p className="text-h5 text-black60">{t("emailNotifications.matchTopicHint")}</p>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-h5 text-white">{t("emailNotifications.matchNotes")}</label>
                <input
                  type="text"
                  value={criteriaForm.notesContains}
                  onChange={(e) =>
                    setCriteriaForm((prev) => ({ ...prev, notesContains: e.target.value }))
                  }
                  placeholder={t("emailNotifications.matchNotesPlaceholder")}
                  className={fieldClass}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-h5 text-white">{t("emailNotifications.matchLogType")}</label>
                <select
                  value={criteriaForm.logType}
                  onChange={(e) =>
                    setCriteriaForm((prev) => ({
                      ...prev,
                      logType: e.target.value as AlertCriteriaForm["logType"],
                    }))
                  }
                  className={fieldClass}
                >
                  <option value="any">{t("emailNotifications.matchLogTypeAny")}</option>
                  <option value="foreground">
                    {t("emailNotifications.matchLogTypeForeground")}
                  </option>
                  <option value="background">
                    {t("emailNotifications.matchLogTypeBackground")}
                  </option>
                </select>
              </div>

              <label className="flex items-start gap-2 text-h5 text-white">
                <input
                  type="checkbox"
                  checked={criteriaForm.matchEnabledClasses}
                  onChange={(e) =>
                    setCriteriaForm((prev) => ({
                      ...prev,
                      matchEnabledClasses: e.target.checked,
                    }))
                  }
                  className="mt-0.5 h-5 w-5 shrink-0 rounded accent-primary2"
                />
                <span>
                  <span className="block">{t("emailNotifications.matchEnabledClasses")}</span>
                  <span className="mt-0.5 block text-black60">
                    {t("emailNotifications.matchEnabledClassesHint")}
                  </span>
                </span>
              </label>

              {!criteriaForm.matchEnabledClasses ? (
                <div className="flex flex-col gap-2">
                  <div>
                    <p className="text-h5 text-white">{t("emailNotifications.matchData")}</p>
                    <p className="mt-0.5 text-h5 text-black60">
                      {t("emailNotifications.matchDataHint")}
                    </p>
                  </div>
                  {criteriaForm.dataRows.map((row, index) => (
                    <div
                      key={`data-row-${index}`}
                      className="flex flex-col gap-2 rounded-r5 border border-black80 bg-black100/40 p-3"
                    >
                      <div className="flex flex-col gap-1.5">
                        <label className="text-h5 text-black40">
                          {t("emailNotifications.matchDataPath")}
                        </label>
                        <input
                          type="text"
                          value={row.path}
                          onChange={(e) => updateDataRow(index, { path: e.target.value })}
                          placeholder={DETECTION_CLASS_ID_PATH}
                          className={fieldClass}
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-h5 text-black40">
                          {t("emailNotifications.matchDataValues")}
                        </label>
                        <input
                          type="text"
                          value={row.valuesText}
                          onChange={(e) =>
                            updateDataRow(index, { valuesText: e.target.value })
                          }
                          placeholder={t("emailNotifications.matchDataValuesPlaceholder")}
                          className={fieldClass}
                        />
                      </div>
                      {criteriaForm.dataRows.length > 1 ? (
                        <button
                          type="button"
                          className="self-start text-h5 text-black40 underline hover:text-white"
                          onClick={() => removeDataRow(index)}
                        >
                          {t("emailNotifications.matchDataRemove")}
                        </button>
                      ) : null}
                    </div>
                  ))}
                  <button
                    type="button"
                    className="self-start text-h5 text-primary2 hover:underline"
                    onClick={addDataRow}
                  >
                    {t("emailNotifications.matchDataAdd")}
                  </button>
                </div>
              ) : null}

              <div className="flex flex-col gap-1.5">
                <label className="text-h5 text-white">
                  {t("emailNotifications.additionalRecipients")}
                </label>
                <input
                  type="text"
                  value={additionalRecipientsTo}
                  onChange={(e) => setAdditionalRecipientsTo(e.target.value)}
                  placeholder={t("emailNotifications.additionalRecipientsPlaceholder")}
                  className={fieldClass}
                />
                <p className="text-h5 text-black60">
                  {t("emailNotifications.additionalRecipientsHint")}
                </p>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-h5 text-white">{t("emailNotifications.maxAttempts")}</label>
                <input
                  type="number"
                  min={1}
                  max={10}
                  value={maxAttempts}
                  onChange={(e) =>
                    setMaxAttempts(Math.min(10, Math.max(1, Number(e.target.value))))
                  }
                  className="w-24 rounded-r5 border border-black80 bg-black70 px-3 py-2.5 text-h5 text-white focus:border-primary2 focus:outline-none"
                />
              </div>
            </div>
          ) : null}
        </section>
      </div>

      {error ? (
        <p className="mt-3 text-h5 text-red-400" role="alert">
          {error}
        </p>
      ) : null}

      <div className="mt-4 flex items-center justify-end gap-2">
        <FunctionalButton
          className="h-8 min-h-8 rounded-r5 border border-black80 bg-black70 px-3 hover:bg-black80 disabled:opacity-50"
          title={t("common:cancel")}
          titleClassName="!normal-case !text-h5 !leading-none !font-normal !text-white"
          onClick={onClose}
          disabled={saving}
        />
        <FunctionalButton
          className="h-8 min-h-8 rounded-r5 border border-primary2 bg-primary2 px-3 hover:bg-primary1 disabled:opacity-50"
          title={
            saving
              ? t("common:saving")
              : isEdit
                ? t("emailNotifications.saveRule")
                : t("emailNotifications.createRule")
          }
          titleClassName="!normal-case !text-h5 !leading-none !font-normal !text-white"
          onClick={() => void handleSubmit()}
          disabled={saving || templates.length === 0}
        />
      </div>
    </AnimatedModal>
  );
};

export default EmailRuleDialog;
